import { useState, useCallback, useEffect } from 'react';
import {
  PlusCircle, Ear, Loader2, AlertCircle, CheckCircle2, RefreshCw, Radio,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import espnowService, {
  type EspNowRouter, type NodeCandidate,
} from '../../services/espnowIrrigation';
import { errorMessage } from '../../utils/apiResponse';
import { nodeNoun } from '../../utils/nodeLabel';
import { useOrgTimezone, formatDateTimeInTz } from '../../utils/timezone';

/**
 * Getting a node into the list without relying on the mesh answering.
 *
 * "Find nodes" is a broadcast. It is unacknowledged, its replies are unicasts
 * racing a fixed timer, and on a slow mesh several hops deep it loses a
 * different subset of nodes every run — so it can never be the only way in. The
 * bridge now has two other routes, and both of them need a UI or they might as
 * well not exist:
 *
 *   Add by address   The address is printed on the radio and the installer is
 *                    standing next to it. This path touches no radio at all, so
 *                    it works with the coordinator unplugged — which is exactly
 *                    the situation where somebody is stuck on a farm unable to
 *                    commission a node. There was previously nothing an
 *                    operator could do in that case except drive home.
 *
 *   Adopt what was   The bridge hears every frame the mesh sends and records
 *   overheard        any 64-bit address that is not one of ours. It will not
 *                    adopt one by itself (only discovery may create a node, so
 *                    a neighbouring farm's radio cannot appear in this list),
 *                    but it reports them — and a node that missed every
 *                    broadcast is transmitting anyway. Discovery therefore gets
 *                    to search over hours instead of over one window, and this
 *                    is where that shows up.
 *
 * Both are deliberately in their own panel rather than buried in the diagnostics
 * button grid: they are things an operator does once per node while installing
 * it, not checks they run when something looks wrong.
 */

interface Props {
  controllerId: string;
  transport?: string | null;
  routers: EspNowRouter[];
  online: boolean;
  /** Refetch the node list once the bridge has confirmed an add. */
  onRefresh?: () => void;
}

export default function NodeEnrolment({
  controllerId, transport, routers, online, onRefresh,
}: Props) {
  const noun = nodeNoun(transport);
  const tz = useOrgTimezone();

  const [mac, setMac] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [queued, setQueued] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<NodeCandidate[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  const knownMacs = routers.map(r => r.router_mac);
  /**
   * The node SET, as a value rather than as an array identity.
   *
   * The page re-polls the node list every 15 seconds and hands down a fresh
   * array each time, so depending on `routers` would refetch the whole event
   * history four times a minute to answer a question whose answer only changes
   * when a node is added or removed. This is that question.
   */
  const knownKey = [...knownMacs].sort().join(',');

  const loadCandidates = useCallback(async () => {
    setLoadingCandidates(true);
    try {
      setCandidates(await espnowService.getNodeCandidates(
        controllerId, knownKey ? knownKey.split(',') : []));
    } catch {
      // An empty candidate list is the normal, healthy state: it means every
      // address this site has heard is already one of its nodes. Not an error.
    } finally {
      setLoadingCandidates(false);
    }
  }, [controllerId, knownKey]);

  useEffect(() => { loadCandidates(); }, [loadCandidates]);

  /** Strip what people actually type when copying an address off a label. */
  function normalise(value: string): string {
    return value.toUpperCase().replace(/[^0-9A-F]/g, '');
  }

  const cleanMac = normalise(mac);
  const macValid = cleanMac.length === 16;

  async function add(address: string, nodeName?: string | null) {
    setBusy(address);
    setError(null);
    setQueued(null);
    try {
      await espnowService.runDiagnostic(controllerId, {
        action: 'ADD_NODE',
        router_mac: address,
        ...(nodeName ? { name: nodeName } : {}),
      });
      setQueued(
        `${address} queued to be added. The bridge picks it up within about 15 seconds `
        + `and will probe it — the result appears under "Recent checks" in mesh `
        + `diagnostics, and the ${noun.oneLower} appears in the list either way.`
      );
      setMac('');
      setName('');
      // The candidate row should stop being offered as soon as it is adopted,
      // and the node list is the parent's to refetch.
      loadCandidates();
      onRefresh?.();
    } catch (err) {
      setError(errorMessage(err, `Could not queue the add for ${address}`));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <PlusCircle className="w-5 h-5" />
          Add {noun.manyLower}
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={loadCandidates} aria-label="Refresh">
          <RefreshCw className={`w-4 h-4 ${loadingCandidates ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>

      <CardContent className="space-y-4">
        {error && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {queued && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-blue-50 border border-blue-200 text-sm text-blue-800">
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{queued}</span>
          </div>
        )}

        {/* ── add by address ─────────────────────────────────────────────── */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            64-bit address
          </label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              value={mac}
              onChange={e => setMac(e.target.value)}
              placeholder="0013A20040AFF5E4"
              spellCheck={false}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-mono
                         focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={`Name (optional) — e.g. "Block 2 valves"`}
              maxLength={20}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm
                         focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Button
              onClick={() => add(cleanMac, name.trim() || null)}
              disabled={!macValid || busy !== null}
            >
              {busy === cleanMac ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
            </Button>
          </div>

          {/* Said before the press, not after: the bridge would take a minute to
              come back with the same complaint. */}
          {mac.trim() !== '' && !macValid && (
            <p className="mt-1 text-xs text-amber-700">
              That is {cleanMac.length} hex character{cleanMac.length === 1 ? '' : 's'} — an
              address is 16, as printed on the radio. Colons, dashes and spaces are fine.
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Reads the number off the radio's label instead of asking the mesh for it, so
            this works even when the coordinator is unplugged or the {noun.oneLower} is
            out of range. It is added offline and confirmed the next time it answers.
          </p>
          {!online && (
            <p className="mt-1 text-xs text-amber-700">
              This controller is offline, so the add will queue and run when the bridge
              reconnects.
            </p>
          )}
        </div>

        {/* ── candidates ─────────────────────────────────────────────────── */}
        <div className="pt-3 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
            <Ear className="w-4 h-4" />
            Heard on this site but not added
          </h4>

          {candidates.length === 0 ? (
            <p className="text-sm text-gray-500 py-1">
              Nothing unaccounted for. Every address this site has heard is already one
              of its {noun.manyLower}.
            </p>
          ) : (
            <>
              <p className="text-xs text-gray-500 mb-2">
                These radios are transmitting on this site's network and are not in the
                list. The bridge will not adopt one by itself — a neighbouring farm's
                radio must never appear here — so adding is a decision for you.
              </p>
              <ul className="space-y-2">
                {candidates.map(c => (
                  <li
                    key={c.router_mac}
                    className="flex items-start justify-between gap-3 p-2 rounded-md border border-gray-200"
                  >
                    <div className="min-w-0">
                      <p className="font-mono text-xs text-gray-800 break-all">
                        {c.router_mac}
                      </p>
                      <p className="text-xs text-gray-500 leading-snug">
                        {c.name ? <span className="font-medium">{c.name} · </span> : null}
                        {/* A node that announced itself is a much stronger
                            signal than one merely overheard: it was reporting a
                            rejoin or somebody was pressing its commissioning
                            button, and it named itself while doing so. */}
                        {c.source === 'node_id'
                          ? <>announced itself{c.detail ? ` (${c.detail})` : ''}</>
                          : 'heard transmitting'}
                        {' · '}
                        {formatDateTimeInTz(c.timestamp, tz)}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => add(c.router_mac, c.name)}
                      disabled={busy !== null}
                      className="shrink-0"
                    >
                      {busy === c.router_mac
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <><Radio className="w-4 h-4 mr-1" />Add</>}
                    </Button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
